export type PageNavigationOption = { page: number; type: 'page' } | { type: 'ellipsis' };

const withEllipses = (pages: number[]): PageNavigationOption[] => {
  return pages.reduce<PageNavigationOption[]>((items, page, index) => {
    const previousPage = pages[index - 1];

    if (index > 0 && page - previousPage === 2) {
      // A single page is hidden between the two, so show it instead of an ellipsis
      items.push({ type: 'page', page: previousPage + 1 });
    } else if (index > 0 && page - previousPage > 1) {
      items.push({ type: 'ellipsis' });
    }

    items.push({ type: 'page', page });

    return items;
  }, []);
};

/**
 * Get pagination options with first and last page always visible.
 * Ellipses are inserted when more than one page is hidden between two visible options.
 */
export const getPageNavigationOptions = (pageCount: number, currentPage: number): PageNavigationOption[] => {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, i) => ({ type: 'page', page: i }));
  }

  const lastPage = pageCount - 1;

  if (currentPage === 0 || currentPage === 1) {
    return withEllipses([0, 1, 2, 3, lastPage]);
  }

  if (currentPage === lastPage || currentPage === lastPage - 1) {
    return withEllipses([0, lastPage - 3, lastPage - 2, lastPage - 1, lastPage]);
  }

  const pages = new Set([0, currentPage - 1, currentPage, currentPage + 1, lastPage]);
  const sortedPages = Array.from(pages)
    .filter((page) => page >= 0 && page <= lastPage)
    .sort((a, b) => a - b);

  return withEllipses(sortedPages);
};
